import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import { AdminContextConsumer } from "../pages/Admin"
import Typography from "@material-ui/core/Typography/Typography";
import Card from "@material-ui/core/Card/Card";
import CardContent from "@material-ui/core/CardContent/CardContent";
import gql from 'graphql-tag';
import * as graphql from '../common/graphql';
import Message from "./Message";
import { withIAM } from "../common/iam";
import * as i18n from "../common/i18n";
import ResponsiveContainer from 'recharts/lib/component/ResponsiveContainer';
import {
    Tooltip, PieChart, Pie, Cell, Legend
} from 'recharts';
import Skeleton from "react-loading-skeleton";

const defaultDeviceData = {
    total: {
        totalCount: 0
    },
    leased: {
        totalCount: 0
    },
    stocked: {
        totalCount: 0
    },
    maintenance: {
        totalCount: 0
    },
    defective: {
        totalCount: 0
    },
    assembly: {
        totalCount: 0
    }
}

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    head: {
        display: "flex",
        justifyContent: "center",
        alignItems: "baseline"
    },
    header: {
        margin: theme.spacing(5)
    },
    formControl: {
        margin: theme.spacing(),
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
});

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#ec0101', '#F4D03F', '#8E44AD', '#E74C3C', '#BDC3C7', '#EC7063', '#0E6655', '#5D6D7E'];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    if (percent * 100 > 10) {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    } else {
        return null
    }
};

const DevicesChart = (props) => {
    const { deviceData } = props;

    const data = [
        { name: 'Vermietet', Anzahl: deviceData.leased.totalCount },
        { name: 'Im Lager', Anzahl: deviceData.stocked.totalCount },
        { name: 'In Reparatur', Anzahl: deviceData.maintenance.totalCount },
        { name: 'In Montage', Anzahl: deviceData.assembly.totalCount },
        { name: 'Defekt', Anzahl: deviceData.defective.totalCount },
    ];

    return (

        <ResponsiveContainer width="99%" height={320}>
            <PieChart width={320} height={320}>
                <Pie
                    data={data}
                    cx={150}
                    cy={200}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    legend={true}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="Anzahl"
                >
                    {
                        data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)
                    }
                </Pie>
                <Legend
                    verticalAlign="bottom"
                    align="left"
                    layout="vertical"
                    wrapperStyle={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #d5d5d5',
                        borderRadius: 3,
                    }}
                    payload={data.map((item, index) => ({
                        id: item.name,
                        type: "square",
                        value: `${item.name}: ${item.Anzahl} ${item.Anzahl === 1 ? 'Gerät' : 'Geräte'}`,
                        color: COLORS[index % COLORS.length],
                    }))}
                />
                <Tooltip />
            </PieChart>
        </ResponsiveContainer>
    );
}


class DeviceStatusreport extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            devicesChartLoading: true,
            data: defaultDeviceData,
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        };
    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    componentDidMount() {
        this.setState({
            loading: false
        });
        this.fetchDeviceStatus()
    }

    fetchDeviceStatus = async () => {
        try {
            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
            query {
                total: devices {
                    totalCount
                }
                leased: devices(
                    where: [{ column: "status", comparator: IS, string: "LEASED" }]

                ) {
                    totalCount
                }
                stocked: devices(
                    where: [{ column: "status", comparator: IS, string: "STOCKED" }]
                ) {
                    totalCount
                }
                maintenance: devices(
                    where: [{ column: "status", comparator: IS, string: "MAINTENANCE" }]
                ) {
                    totalCount
                }
                defective: devices(
                    where: [{ column: "status", comparator: IS, string: "DEFECTIVE" }]
                ) {
                    totalCount
                }
                assembly: devices(
                    where: [{ column: "status", comparator: IS, string: "ASSEMBLY" }]
                ) {
                    totalCount
                }
            }`,
                fetchPolicy: "no-cache"
            });

            const searchResult = {
                data: result.data
            }

            this.handleDeviceStatusChange(searchResult);
        }
        catch (error) {
            this.showMessage(error.message, "error");
        }
    }

    handleDeviceStatusChange = async (deviceCounters) => {
        this.setState({
            deviceData: deviceCounters.data,
            devicesChartLoading: false
        })

    }

    render() {

        const { classes } = this.props;

        return (
            <React.Fragment>
                {this.state.loading ? <div /> : (
                    <AdminContextConsumer>
                        {({ onLink }) => (
                            <React.Fragment>
                                <Card width={320} height={320}>
                                    <CardContent>

                                        <Typography variant="h5" component="h2">
                                            {i18n.get("devicesStatusReport.h2")}
                                        </Typography>

                                        {this.state.devicesChartLoading ?
                                            <section>
                                                <Skeleton height={100} width={180} style={{ marginTop: '210px', verticalAlign: 'bottom' }} />
                                                <Skeleton circle={true} height={200} width={200} style={{ marginLeft: '35px' }} />
                                            </section>
                                            :
                                            <DevicesChart deviceData={this.state.deviceData} />
                                        }

                                    </CardContent>
                                </Card>
                            </React.Fragment>
                        )}
                    </AdminContextConsumer>

                )}

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />

            </React.Fragment>
        )
    }
}

DeviceStatusreport.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(DeviceStatusreport))